'use client';

import React from 'react';

// Simple toast context implementation
interface ToastProps {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: 'default' | 'destructive' | 'success' | 'warning' | 'info';
  action?: React.ReactNode;
  duration?: number;
}

interface ToastContextType {
  toast: (props: ToastProps) => { id: string; dismiss: () => void };
  dismiss: (toastId?: string) => void;
}

const ToastContext = React.createContext<ToastContextType | null>(null);

export function ClientToastWrapper({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Array<{ id: string; props: ToastProps }>>([]);

  const toast = (props: ToastProps) => {
    const id = props.id || Math.random().toString(36).substr(2, 9);
    
    // Add to toasts array
    setToasts(prev => [...prev, { id, props }]);
    
    // Auto-dismiss after duration
    const duration = props.duration || 5000;
    setTimeout(() => {
      dismiss(id);
    }, duration);
    
    return {
      id,
      dismiss: () => dismiss(id)
    };
  };

  const dismiss = (toastId?: string) => {
    if (toastId) {
      setToasts(prev => prev.filter(toast => toast.id !== toastId));
    } else {
      setToasts([]);
    }
  };

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {/* Simple toast display */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(({ id, props }) => (
          <div
            key={id}
            className={`
              bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-lg shadow-lg p-4 min-w-[300px] max-w-[400px] relative
              ${props.variant === 'destructive' ? 'border-red-200 bg-red-50' : ''}
              ${props.variant === 'success' ? 'border-green-200 bg-green-50' : ''}
              ${props.variant === 'warning' ? 'border-yellow-200 bg-yellow-50' : ''}
            `}
          >
            {props.title && <div className="font-semibold mb-1">{props.title}</div>}
            {props.description && <div className="text-sm text-gray-600 dark:text-gray-300">{props.description}</div>}
            <button
              onClick={() => dismiss(id)}
              className="absolute top-2 right-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 w-6 h-6 flex items-center justify-center"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Export the hook that components expect
export const useToast = (): ToastContextType => {
  const context = React.useContext(ToastContext);
  if (!context) {
    console.error("useToast hook called outside of a ClientToastWrapper. Ensure ClientToastWrapper is placed correctly in your component tree (e.g., in client-providers.tsx).");
    throw new Error('useToast must be used within a ClientToastWrapper');
  }
  return context;
};
